import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { analytics } from '@helpers/analytics'
import { productListViewed } from '@helpers/products'
import useABtests from '@helpers/hooks/useABtests'
import CollectionDetails from '@components/collection/CollectionDetails'
import Hero from './Hero'
import CollectionWrapper from './Collection/index'
import CollectionContext from './collectionContext'
import abtestCollectionsProductBreakdownData from './abtestCollectionsProductBreakdown.json'
import styles from './styles.module.scss'
import {
  CollectionMenuLinks,
  CollectionMenuLinksProps
} from './CollectionMenuLinks'
import type {
  Collection,
  CollectionConfig,
  CollectionListMeta
} from 'types/collection'
import { FilterState } from '@store/redux/reducers/filterReducer'
import { RootState } from '@store/redux/reducers'
import { ShopLocale } from 'types/locales'
import { ProductsBreakdownProps } from '@components/ui/ProductsBreakdown'
import { CardItem } from '@components/ui/ProductsBreakdown/PBGrid'
import CollectionFilter from './CollectionFilter'
import { FilterBuild } from './CollectionFilter/FiltersSidePanel/types'
import { FilterOption } from 'types/filters'
import {
  resetDynamicFilters,
  selectDynamicFilter
} from '@store/redux/actions/filterActions'
import useLocalStorage from '@helpers/hooks/useLocalStorage'
import { useFetchVariants } from './hooks/useFetchVariants'
import { useFilterContext } from './hooks/useFilterContext'
import FilterToggleButton from './CollectionFilter/FiltersSidePanel/FilterToggleButton'
import { getIsFirstTimeUser } from '@helpers/session/user'
import { getCustomerFitCondensed } from '@store/redux/selectors/customerFitSelectors'
import { Fit } from 'types/fit'
import { BUILD } from '@consts/index'
import Head from 'next/head'
import Meta from '@components/Meta'
import getCollectionProductData from '@helpers/filters/getCollectionProductData'

const COLLECTION_PRODUCTS_BREAKDOWN_AB_TEST_ID = 'plp-cf-styles-v1'

type ProductBreakdownCategory = {
  [category: string]: Partial<CardItem>[]
}

const abtestProductBreakdownJSON: Record<string, ProductBreakdownCategory> = {
  ...abtestCollectionsProductBreakdownData
} as const

const shopLocale =
  (process.env.SHOP_LOCALE?.toLowerCase() as ShopLocale) || 'gb'

const abtestCollectionsProductBreakdownDataFromLocale: ProductBreakdownCategory =
  abtestProductBreakdownJSON[shopLocale]

const ABTEST_PRODUCTS_BREAKDOWN_ITEMS_BY_CATEGORY = {
  trousers: abtestCollectionsProductBreakdownDataFromLocale.trousers,
  denim: abtestCollectionsProductBreakdownDataFromLocale.denim,
  shorts: abtestCollectionsProductBreakdownDataFromLocale.shorts,
  tops: abtestCollectionsProductBreakdownDataFromLocale.tops,
  'mens-gift-edit': abtestCollectionsProductBreakdownDataFromLocale.gifts
}

const ProductsBreakdown = dynamic(
  () => import('@components/ui/ProductsBreakdown')
)
const ProductsPromo = dynamic(() => import('@components/ui/ProductsPromo'))

function getCollectionProductsCount({
  collections,
  filterOptions,
  variantExist
}: {
  collections: Collection[]
  filterOptions: FilterOption[]
  variantExist: boolean
}) {
  const { collectionsWithFilteredProducts } = getCollectionProductData({
    collections,
    filterOptions,
    variantExist
  })
  return collectionsWithFilteredProducts.reduce((total, collection) => {
    return total + collection.items.length
  }, 0)
}

type CollectionListProps = {
  collectionHandle: Collection['handle']
  collectionHero?: {
    badge: {
      title: string
    }
  }
  collectionData: {
    title: string
    byline: string
    description: string
    collections: Collection[]
    collectionConfig?: CollectionConfig
    meta: CollectionListMeta
  }
  productsBreakdown: ProductsBreakdownProps
  navigationLinks: CollectionMenuLinksProps['navigationLinks']
  productIds: number[]
  productTypes: string[]
  filterBuild: FilterBuild
}

function CollectionList({
  collectionHandle,
  collectionHero,
  collectionData,
  productsBreakdown,
  navigationLinks,
  productIds,
  productTypes,
  filterBuild
}: CollectionListProps) {
  const router = useRouter()
  const dispatch = useDispatch()
  const { title, byline, collections, meta, description } = collectionData
  const { category: queryCategory, collectonHandle: queryCollectionHandle } =
    router.query as { category: string; collectonHandle: string }

  const { fit }: { fit: Partial<Fit> } = useSelector(getCustomerFitCondensed)

  const { updatedCollections, variantExist } = useFetchVariants(
    productIds,
    productTypes,
    collections
  )

  const {
    filterCategory,
    setFilterCategory,
    collectionsByFiltersCategory,
    filtersCategories,
    isFiltersOpen,
    closeFilters,
    openFilters,
    showFilters
  } = useFilterContext({
    metaFilters: meta.filters,
    collections: updatedCollections
  })

  const filterOptions = useSelector<RootState, FilterState['filterOptions']>(
    state => state.filters.filterOptions
  )

  const totalResults = getCollectionProductsCount({
    collections:
      filterCategory === 'all'
        ? updatedCollections
        : collectionsByFiltersCategory,
    filterOptions,
    variantExist
  })

  const [isSavedFitApplied, setIsSavedFitApplied] = useLocalStorage(
    'plp_filters_saved_fit_applied',
    false
  )

  const [isApplyFitActive, setIsApplyFitActive] =
    useState<boolean>(isSavedFitApplied)

  const collection = !collectionsByFiltersCategory.length
    ? updatedCollections
    : collectionsByFiltersCategory

  const { testsVariants } = useABtests()

  const collectionProductBreakdownAbTestVariant =
    testsVariants[COLLECTION_PRODUCTS_BREAKDOWN_AB_TEST_ID]
  const productBreakdownData =
    collectionProductBreakdownAbTestVariant === 'variant-2'
      ? {
          category: null,
          visible: true,
          title:
            collectionHandle !== 'mens-gift-edit'
              ? 'Explore our styles'
              : 'Our top gifts',
          toggle: false,
          productType: '',
          firstTimeUser: getIsFirstTimeUser(),
          categories: {
            items:
              ABTEST_PRODUCTS_BREAKDOWN_ITEMS_BY_CATEGORY[
                collectionHandle as keyof typeof ABTEST_PRODUCTS_BREAKDOWN_ITEMS_BY_CATEGORY
              ]
          }
        }
      : productsBreakdown

  useEffect(() => {
    if (queryCategory && filtersCategories.includes(queryCategory)) {
      setFilterCategory(queryCategory)
    } else {
      setFilterCategory('all')
    }
  }, [queryCategory, filtersCategories, setFilterCategory])

  useEffect(() => {
    if (variantExist) {
      const eventProducts = collection.flatMap(collectionEntry => {
        return collectionEntry.items.map((product, index) => ({
          quantity: 1,
          name: collectionEntry.title,
          color: product.title,
          price: parseFloat(product.price),
          position: index + 1,
          url: product.handle,
          image_url: product.images[0]?.src,
          brand: 'SPOKE',
          product_id: product.product_feed?.product_id
        }))
      })
      productListViewed({
        context: 'Collection',
        listId: collectionHandle,
        listName: title,
        products: eventProducts
      })
    }
  }, [variantExist, collection, collectionHandle, title])

  const shouldShowProductsBreakdown =
    productsBreakdown?.categories.items.length > 0 &&
    collectionProductBreakdownAbTestVariant !== 'variant-1'

  const collectionContext = useMemo(
    () => ({
      collectionHandle,
      hasMultipleProductTypes: collection?.length > 1,
      collectionMainTitle: title,
      closeFilters,
      openFilters,
      isFiltersOpen,
      isApplyFitActive
    }),
    [isFiltersOpen]
  )

  const handleFilterClear = () => {
    setIsApplyFitActive(false)
    setIsSavedFitApplied(false)
    dispatch(resetDynamicFilters())
  }

  const handleFilterApply = useCallback(
    (options: FilterOption[]) => {
      const isSavedFitValid = filterOptions.every(
        option =>
          fit[option.type as keyof Fit] ===
          String(option.value.replace(BUILD, '').trim())
      )
      setIsSavedFitApplied(isSavedFitValid)
      dispatch(resetDynamicFilters())
      dispatch(selectDynamicFilter(options))
      closeFilters()
    },
    [filterOptions, closeFilters, dispatch, fit, setIsSavedFitApplied]
  )

  const handleApplySavedFitClick = (state: boolean) => {
    setIsSavedFitApplied(state)
    setIsApplyFitActive(state)
  }

  return (
    <>
      <Head>
        <Meta includeNoFollow={meta.page_crawl} />
      </Head>
      <div style={{ minHeight: 800 }}>
        <CollectionContext.Provider value={collectionContext}>
          <CollectionMenuLinks navigationLinks={navigationLinks} />
          <Hero
            collectionHero={collectionHero}
            title={title}
            byline={byline}
            productTypes={productTypes}
          >
            {showFilters && (
              <FilterToggleButton
                totalResults={totalResults}
                filterBuild={filterBuild}
                filterOptions={filterOptions}
              />
            )}
          </Hero>
          {shouldShowProductsBreakdown && (
            <div className={styles.collectionProductsBreakdown}>
              <ProductsBreakdown
                {...productBreakdownData}
                category="Collection Page"
              />
            </div>
          )}
          <section className={styles.collectionContainer}>
            <div className={styles.collectionGridContainer}>
              {showFilters && (
                <div className={styles.collectionFilterWrapper}>
                  <CollectionFilter
                    filterCategory={filterCategory}
                    filtersCategories={filtersCategories}
                    filterBuild={filterBuild}
                    onFilterClear={handleFilterClear}
                    onFilterApply={handleFilterApply}
                    onApplyFitToggle={handleApplySavedFitClick}
                    totalResults={totalResults}
                    isMultiCollection={meta.filters?.multi || false}
                  />
                </div>
              )}
              <div className={styles.collectionWrapper}>
                <CollectionWrapper
                  collections={collection}
                  header={meta.header}
                  filterOptions={filterOptions}
                  variantExist={variantExist}
                  showProductType={meta.show_product_type}
                />
              </div>
            </div>
          </section>

          <div className={styles.collection}>
            {description && <CollectionDetails copy={description} />}
            <div className={styles.ProductsPromo__wrapper}>
              <ProductsPromo
                pageHandle={queryCollectionHandle}
                onLinkClick={productsPromotedCollection => {
                  analytics('Click', {
                    category: 'Collection Page',
                    label: 'Collection Promo',
                    collection_promoted: productsPromotedCollection
                  })
                }}
              />
            </div>
          </div>
        </CollectionContext.Provider>
      </div>
    </>
  )
}

export default CollectionList
